import { useMemo } from 'react';

import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useIsFindOrLinkPatientEnabled } from '@heidi/patients/flags';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { isViewingOtherSessionAtom } from '@heidi/session/data';
import { useAtomValue } from 'jotai';

import { buildSearchableProfiles, searchLocalProfiles } from '@heidi/patients';
import type { PatientProfileSearchResult } from '@heidi/patients/contracts';

/**
 * Above this roster size, skip local search entirely and fall back to
 * BE-only behaviour (identical to today). A linear scan of 2000
 * pre-normalised profiles per keystroke is sub-millisecond; beyond it we
 * stop pretending the full-roster fetch is a sensible search index —
 * those (EHR-scale) users need the BE-side fix, not a bigger client scan.
 */
const LOCAL_SEARCH_MAX_PROFILES = 2000;

const LOCAL_RESULT_LIMIT = 10;

interface UseLocalPatientSearchArgs {
  /** Live (undebounced) query — local results recompute synchronously per keystroke. */
  query: string;
  enabled: boolean;
  /**
   * The session the BE search is scoped to (same value the caller
   * passes to `useSearchPatientProfiles`). Used ONLY for the
   * scope-alignment guard below — see `isScopeAligned`.
   */
  sessionId?: string;
}

interface UseLocalPatientSearchResult {
  /**
   * Instant matches from the cached roster for the live query. Empty
   * when local search is inactive — callers then behave exactly as
   * before this layer existed.
   */
  localResults: PatientProfileSearchResult[];
  /** False when flag off, roster not loaded, roster above the cutoff, or scope unaligned. */
  isLocalActive: boolean;
}

/**
 * Local-first layer of the find-or-link search: matches the live query
 * against the already-cached patient roster (`useQueryPatientProfiles`,
 * which is scope-aware for the direct-share / team-review cases) so the
 * dropdown has results on the FIRST keystroke — below the Atlas index's
 * gram minimums and without the debounce + network wait.
 *
 * Display-only: consumers merge these rows with BE results (see
 * `useFindOrLinkSearch`) and the auto-link rule stays BE-canonical.
 *
 * Ships under the same `release-scribe-enable-patient-find-or-link`
 * flag as the combobox itself (PM call: no separate child flag) — so
 * there is no independent kill switch; disabling local search means
 * disabling find-or-link or reverting the code.
 */
export function useLocalPatientSearch({
  query,
  enabled,
  sessionId,
}: UseLocalPatientSearchArgs): UseLocalPatientSearchResult {
  const isFlagEnabled = useIsFindOrLinkPatientEnabled();
  // The controller mounts eagerly in the session header, so the roster
  // fetch is gated on "this search could actually run" (flag on AND the
  // caller's enabled — false on already-linked sessions). Without the
  // gate, every session view fired the unpaginated GET /patient-profiles/
  // even where search can never happen (review finding). When another
  // consumer has already fetched, this still reads the shared cache.
  const { data: profiles } = useQueryPatientProfiles({
    enabled: isFlagEnabled && enabled,
  });

  // SCOPE-ALIGNMENT GUARD. `useQueryPatientProfiles` resolves whose
  // roster to fetch from GLOBAL route state, and there are two states
  // in which that roster is NOT the signed-in user's own:
  //
  // 1. `isViewingOtherSessionAtom` — set from RAW NON-OWNERSHIP of the
  //    open session (`kinde_gp_id` differs from the signed-in user),
  //    NOT from direct-share state specifically. It trips for a share
  //    viewer AND for an assistant opening a clinician's session (the
  //    DEV-12028 bypass); in both cases the ambient roster is the
  //    session OWNER's, keyed on `useCurrentSessionId()`. Do not
  //    "tighten" this to direct-share semantics — that would reopen
  //    the assistant gap.
  // 2. Team-review mode (`isReviewingOtherUser`) — the reviewed team
  //    member's roster.
  //
  // The BE search, by contrast, is scoped to the caller-supplied
  // `sessionId`. Those agree on the in-session header (both derive
  // from the open session), but the session-list popover is persistent
  // sidebar chrome: a caller can target a session that does NOT belong
  // to whoever the ambient roster belongs to — surfacing that roster's
  // patient names against a differently-scoped BE search would be a
  // privacy bug. Whenever the ambient roster is foreign, only run
  // local search if the caller's target IS the open session (the one
  // case where both scopes provably resolve to the same owner);
  // otherwise fall back to BE-only, which is correctly scoped
  // server-side.
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const currentSessionId = useCurrentSessionId();
  const isAmbientRosterForeign = isViewingOtherSession || isReviewingOtherUser;
  const isScopeAligned =
    !isAmbientRosterForeign || (!!sessionId && sessionId === currentSessionId);

  const isLocalActive =
    isFlagEnabled &&
    enabled &&
    isScopeAligned &&
    !!profiles &&
    profiles.length > 0 &&
    profiles.length <= LOCAL_SEARCH_MAX_PROFILES;

  const searchables = useMemo(
    () => (isLocalActive && profiles ? buildSearchableProfiles(profiles) : []),
    [isLocalActive, profiles]
  );

  const localResults = useMemo(
    () =>
      isLocalActive ? searchLocalProfiles(searchables, query, LOCAL_RESULT_LIMIT) : [],
    [isLocalActive, searchables, query]
  );

  return { localResults, isLocalActive };
}
